import { cloneMap, DataModel, DataModelBuilder, isSameMap, mapToData, SerializationContext, SerializedData } from '../../dto/dto.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { TicketType } from '../ticket-type.js';
import { EventTicket } from '../event-ticket.js';
import { VersionRange } from '../../core/version-range.js';
import { ThirdPartyEventTicket } from '../third-party-event-ticket.js';

export class ScanStatistics extends DataModel {
  static readonly TYPE_ID: string = 'dae319c7fa56484eb0c323f2d3d5dbc2';
  static readonly SCHEMA_VERSION: number = 3;

  constructor(
    public scannedNormalTickets: Map<string, number>,
    public scannedProtocolTickets: Map<string, number>,
    public scannedHardTickets: Map<string, number>,
    public scannedThirdPartyTickets: Map<string, number>,
    public scannedTicketsBy: Map<string, number>,
    public totalScannedTickets: number,
  ) {
    super();
  }

  get $typeId(): string {
    return ScanStatistics.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, ScanStatistics.SCHEMA_VERSION);
  }

  static empty(): ScanStatistics {
    return new ScanStatistics(
        new Map<string, number>(),
        new Map<string, number>(),
        new Map<string, number>(),
        new Map<string, number>(),
        new Map<string, number>(),
        0,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): ScanStatistics {
    return new ScanStatistics(
        MapUtils.getMap<string, number>(map, 'scannedNormalTickets', (k, v) => {
          return { key: k, value: Number(v) };
        }),
        MapUtils.getMap<string, number>(map, 'scannedProtocolTickets', (k, v) => {
          return { key: k, value: Number(v) };
        }),
        MapUtils.getMap<string, number>(map, 'scannedHardTickets', (k, v) => {
          return { key: k, value: Number(v) };
        }),
        MapUtils.getMap<string, number>(map, 'scannedThirdPartyTickets', (k, v) => {
          return { key: k, value: Number(v) };
        }),
        MapUtils.getMap<string, number>(map, 'scannedTicketsBy', (k, v) => {
          return { key: k, value: Number(v) };
        }),
        MapUtils.get<number>(map, 'totalScannedTickets'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'scannedNormalTickets': mapToData(this.scannedNormalTickets, (k, v) => {
        return { key: k.toString(), value: v };
      }),
      'scannedProtocolTickets': mapToData(this.scannedProtocolTickets, (k, v) => {
        return { key: k.toString(), value: v };
      }),
      'scannedHardTickets': mapToData(this.scannedHardTickets, (k, v) => {
        return { key: k.toString(), value: v };
      }),
      'scannedThirdPartyTickets': mapToData(this.scannedThirdPartyTickets, (k, v) => {
        return { key: k.toString(), value: v };
      }),
      'scannedTicketsBy': mapToData(this.scannedTicketsBy, (k, v) => {
        return { key: k.toString(), value: v };
      }),
      'totalScannedTickets': this.totalScannedTickets,
    };
  }

  clone(): this {
    return new ScanStatistics(
        cloneMap(this.scannedNormalTickets),
        cloneMap(this.scannedProtocolTickets),
        cloneMap(this.scannedHardTickets),
        cloneMap(this.scannedThirdPartyTickets),
        cloneMap(this.scannedTicketsBy),
        this.totalScannedTickets,
    ) as this;
  }

  isSame(other: ScanStatistics) {
    if(this === other) {
      return true;
    }

    return isSameMap(other.scannedNormalTickets, this.scannedNormalTickets) &&
      isSameMap(other.scannedProtocolTickets, this.scannedProtocolTickets) &&
      isSameMap(other.scannedHardTickets, this.scannedHardTickets) &&
      isSameMap(other.scannedThirdPartyTickets, this.scannedThirdPartyTickets) &&
      isSameMap(other.scannedTicketsBy, this.scannedTicketsBy) &&
      other.totalScannedTickets === this.totalScannedTickets;
  }

  addTicket(ticket: EventTicket) {
    const ticketId = ticket.templateId;
    if(ticket.ticketType === TicketType.HardTicket) {
      if(!this.scannedHardTickets.has(ticketId)) {
        this.scannedHardTickets.set(ticketId, 0);
      }

      const scans = this.scannedHardTickets.get(ticketId)!;
      this.scannedHardTickets.set(ticketId, scans + 1);
    }
    else if(ticket.ticketType === TicketType.Protocol) {
      if(!this.scannedProtocolTickets.has(ticketId)) {
        this.scannedProtocolTickets.set(ticketId, 0);
      }

      const scans = this.scannedProtocolTickets.get(ticketId)!;
      this.scannedProtocolTickets.set(ticketId, scans + 1);
    }
    else {
      if(!this.scannedNormalTickets.has(ticketId)) {
        this.scannedNormalTickets.set(ticketId, 0);
      }

      const scans = this.scannedNormalTickets.get(ticketId)!;
      this.scannedNormalTickets.set(ticketId, scans + 1);
    }

    const scannerId = ticket.scannerId ?? '';
    if(!this.scannedTicketsBy.has(scannerId)) {
      this.scannedTicketsBy.set(scannerId, 0);
    }

    const scans = this.scannedTicketsBy.get(scannerId)!;
    this.scannedTicketsBy.set(scannerId, scans + 1);

    this.totalScannedTickets++;
  }

  addThirdPartyTicket(ticket: ThirdPartyEventTicket) {
    const ticketId = ticket.templateId;
    if(!this.scannedThirdPartyTickets.has(ticketId)) {
      this.scannedThirdPartyTickets.set(ticketId, 0);
    }

    const ticketScans = this.scannedThirdPartyTickets.get(ticketId)!;
    this.scannedThirdPartyTickets.set(ticketId, ticketScans + 1);

    const scannerId = ticket.scannerId ?? '';
    if(!this.scannedTicketsBy.has(scannerId)) {
      this.scannedTicketsBy.set(scannerId, 0);
    }

    const staffScans = this.scannedTicketsBy.get(scannerId)!;
    this.scannedTicketsBy.set(scannerId, staffScans + 1);

    this.totalScannedTickets++;
  }
}

export class ScanStatisticsBuilder extends DataModelBuilder<ScanStatistics> {
  build(context: SerializationContext, map: SerializedData): ScanStatistics {
    return ScanStatistics.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(ScanStatistics.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(ScanStatistics.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // Version 2: Parameter `scannedProtocolTickets` of type `Map<String, int>` was introduced.
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'scannedProtocolTickets', {});
      version = 2;
    }

    // Version 3: Parameter `scannedThirdPartyTickets` of type `Map<String, int>` was introduced.
    if(version < 3) {
      MapUtils.setIfNullOrUndefined(map, 'scannedThirdPartyTickets', {});
      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
